import { RequestsService } from '../requests/requests.service';
import { OutstandingRequest } from '../model/session';
import { OidcClientWrapper } from '../wasm/oidc_client.service';
import { SessionEncryptionWrapper } from '../wasm/session_encryption.service';
import { IdentificationRequest } from '../model/identification_request';

import { v4 as uuid } from 'uuid';
import {
	IdentificationRequestPayload,
	IdentificationType
} from '../model/identification_request_payload';
import { Registration } from '../model/registration';
import { VerificationStatusDataSource } from '../status/status.service';
import { IdentificationRequestStates } from '../model/identification_request_states';
import { Injectable } from '@angular/core';
import { LoginService } from '../login/login.service';
import { firstValueFrom } from 'rxjs';
import { IdentificationRequestStatusTips } from '../model/identification_request_status_tips';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class VerificationService {
	// session storage keys
	static readonly MMMI_REQUESTS_PENDING = 'mmmi_req_pending';
	static readonly MMMI_REQUESTS_HANDLED = 'mmmi_req_handled';

	private readonly pendingRequests: OutstandingRequest[];
	private readonly handledRequests: string[];

	constructor(
		private readonly loginService: LoginService,
		private readonly statusService: VerificationStatusDataSource,
		private readonly requestsService: RequestsService,
		private readonly oidcClient: OidcClientWrapper,
		private readonly sessionEncryption: SessionEncryptionWrapper
	) {
		this.pendingRequests = JSON.parse(
			sessionStorage.getItem(VerificationService.MMMI_REQUESTS_PENDING) ?? '[]'
		);
		this.handledRequests = JSON.parse(
			sessionStorage.getItem(VerificationService.MMMI_REQUESTS_HANDLED) ?? '[]'
		);
	}

	saveModels() {
		sessionStorage.setItem(VerificationService.MMMI_REQUESTS_PENDING, JSON.stringify(
			this.pendingRequests))
		sessionStorage.setItem(VerificationService.MMMI_REQUESTS_HANDLED, JSON.stringify(
			this.handledRequests))
	}

	static dropEverything() {
		sessionStorage.removeItem(VerificationService.MMMI_REQUESTS_PENDING);
		sessionStorage.removeItem(VerificationService.MMMI_REQUESTS_HANDLED);
	}

	async verify(id?: string | null) {
		if (id != null) {
			console.info(`initiating verification of ${id}`);

			// Display NEW status for generated verification request
			const verificationId = uuid();
			this.statusService.initVerificationRequest(
				id,
				verificationId,
				IdentificationType.REQUEST
			);

			this.requestsService
				.getUser(this.loginService.apiToken ?? 'an invalid token', { userId: id })
				.subscribe({
					next: registration => {
						if (
							this.loginService.idToken != null &&
							this.loginService.id != null
						) {
							const nonce = uuid();
							this.pendingRequests.push({
								id,
								jwtHash: registration.jwtHash,
								nonce,
								verificationId,
								type: IdentificationType.REQUEST
							});
							this.saveModels();
							this.sendIdentificationRequest(
								this.loginService.id,
								this.loginService.idToken,
								id,
								nonce,
								registration,
								verificationId,
								IdentificationType.REQUEST
							)
								.then(() => {
									this.statusService.updateVerificationRequest(
										verificationId,
										IdentificationType.REQUEST,
										IdentificationRequestStates.PENDING,
										IdentificationRequestStatusTips.pending
									);
								})
								.catch(er => {
									const tip = this.handleHttpError(er);
									this.statusService.updateVerificationRequest(
										verificationId,
										IdentificationType.REQUEST,
										IdentificationRequestStates.ERROR,
										tip
									);
								});
						}
					},
					error: er => {
						const tip = this.handleHttpError(er);
						this.statusService.updateVerificationRequest(
							verificationId,
							IdentificationType.REQUEST,
							IdentificationRequestStates.ERROR,
							tip
						);
					}
				});
		}
	}

	handleMessage(msg: IdentificationRequest) {
		var payloadtype: string = ""
		this.sessionEncryption
			.decrypt(this.loginService.keypair?.privateKey ?? '', msg.payload)
			.then(ptxt => {
				const payload: IdentificationRequestPayload = JSON.parse(ptxt);
				payloadtype = "" + payload.type
				if (!this.handledRequests.includes(msg.requestId + payload.type)) {
					const matchingRequests = this.pendingRequests.filter(
						pendingRequest =>
							pendingRequest.verificationId === msg.verificationId
					);
					if (matchingRequests.length == 0) {
						console.info(
							`handling verification request [req-id: ${msg.requestId}; ver-id: ${msg.verificationId}]`
						);
						void this.handleVerificationRequest(msg.verificationId, payload);
					} else {
						for (let matchingRequest of matchingRequests) {
							if (
								matchingRequest != null &&
								payload.type == IdentificationType.RESPONSE
							) {
								console.info(
									`handling verification response [req-id: ${msg.requestId}; ver-id: ${msg.verificationId}]`
								);
								void this.handleVerificationResponse(matchingRequest, payload);
							} else if (payload.type == IdentificationType.REQUEST) {
								console.info(
									`handling verification request [req-id: ${msg.requestId}; ver-id: ${msg.verificationId}]`
								);
								void this.handleVerificationRequest(
									msg.verificationId,
									payload
								);
							}
						}
					}
				} else {
					console.info(
						`ignoring previously seen message [req-id: ${msg.requestId}; ver-id: ${msg.verificationId}]`
					);
				}
			})
			.catch(reason =>
				console.error(
					`failed to decrypt message [req-id: ${msg.requestId}; ver-id: ${msg.verificationId}]: `,
					reason
				)
			)
			.finally(() => {
				this.handledRequests.push(msg.requestId + payloadtype)
				this.saveModels()
			}
			);
	}

	private async handleVerificationRequest(
		verificationId: string,
		payload: IdentificationRequestPayload
	) {
		this.requestsService
			.getUser(this.loginService.apiToken ?? 'an invalid token', {
				userId: payload.id
			})
			.subscribe({
				next: registration => {
					this.statusService.initVerificationRequest(
						payload.id,
						verificationId,
						IdentificationType.RESPONSE
					);
					this.loginService
						.hash(payload.jwt)
						.then(jwtHash => {
							if (registration.jwtHash === jwtHash) {
								this.oidcClient
									.decode_token(payload.jwt)
									.then(jwtClaims => {
										this.respondToRequest(
											jwtClaims,
											payload,
											registration,
											verificationId
										);
									})
									.catch(reason => {
										console.error('failed to verify received JWT: ', reason);
										this.statusService.updateVerificationRequest(
											verificationId,
											IdentificationType.RESPONSE,
											IdentificationRequestStates.ERROR,
											IdentificationRequestStatusTips.failed
										);
									});
							} else {
								console.error(
									'failed to match computed jwt hash to registered hash'
								);
								this.statusService.updateVerificationRequest(
									verificationId,
									IdentificationType.RESPONSE,
									IdentificationRequestStates.ERROR,
									IdentificationRequestStatusTips.failed
								);
							}
						})
						.catch(reason => {
							console.error('failed to compute JWT hash: ', reason);
							this.statusService.updateVerificationRequest(
								verificationId,
								IdentificationType.RESPONSE,
								IdentificationRequestStates.ERROR,
								IdentificationRequestStatusTips.failed
							);
						});
				},
				error: er => {
					const tip = this.handleHttpError(er);
					this.statusService.updateVerificationRequest(
						verificationId,
						IdentificationType.RESPONSE,
						IdentificationRequestStates.ERROR,
						tip
					);
				}
			});
	}
	private parseAcr(acr: string | undefined): number | undefined {
		// urn:qoa.eiam.admin.ch:names:tc:ac:classes:30
		if (acr == null) {
			return 0;
		}
		const values = acr.split(':');
		const acrValue = values.at(-1);
		if (acrValue == null) {
			return 0;
		}
		return parseInt(acrValue);
	}
	private respondToRequest(
		jwtClaims: string,
		payload: IdentificationRequestPayload,
		registration: Registration,
		verificationId: string
	) {
		const claims = JSON.parse(jwtClaims);
		const keys: (keyof typeof claims)[] = environment.claims;
		if (this.loginService.id != null && this.loginService.idToken != null) {
			const acr = this.parseAcr(claims.acr);
			// TODO: put acr value into constant
			if (acr && acr < environment.minimum_acr) {
				this.statusService.updateVerificationRequest(
					verificationId,
					IdentificationType.RESPONSE,
					IdentificationRequestStates.FAILED,
					IdentificationRequestStatusTips.weak_security,
					...keys.map(key => claims[key])
				);
				console.error('ACR value is too low');
				return;
			}
			this.sendIdentificationRequest(
				this.loginService.id,
				this.loginService.idToken,
				payload.id,
				payload.nonce,
				registration,
				verificationId,
				IdentificationType.RESPONSE
			)
				.then(() => {
					this.statusService.updateVerificationRequest(
						verificationId,
						IdentificationType.RESPONSE,
						IdentificationRequestStates.RESPONDED,
						IdentificationRequestStatusTips.responded,

						...keys.map(key => claims[key])
					);
				})
				.catch(er => {
					const tip = this.handleHttpError(er);
					this.statusService.updateVerificationRequest(
						verificationId,
						IdentificationType.REQUEST,
						IdentificationRequestStates.ERROR,
						tip,
						claims.displayName,
						claims.userName,
						claims.amr
					);
				});
		} else {
			console.error(
				'Cannot construct verification response: missing id or idToken data'
			);
			this.statusService.updateVerificationRequest(
				verificationId,
				IdentificationType.RESPONSE,
				IdentificationRequestStates.ERROR,
				IdentificationRequestStatusTips.other,
				...keys.map(key => claims[key])
			);
		}
	}

	/* eslint-disable max-lines-per-function */
	private async handleVerificationResponse(
		request: OutstandingRequest,
		payload: IdentificationRequestPayload
	) {
		if (request.id === payload.id && request.nonce === payload.nonce) {
			this.loginService
				.hash(payload.jwt)
				.then(jwtHash => {
					if (request.jwtHash === jwtHash) {
						this.oidcClient
							.decode_token(payload.jwt)
							.then(jwtClaims => {
								const claims = JSON.parse(jwtClaims);
								const keys: (keyof typeof claims)[] = environment.claims;
								const acr = this.parseAcr(claims.acr);
								// TODO: put acr value into constant
								if (acr && acr < environment.minimum_acr) {
									this.statusService.updateVerificationRequest(
										request.verificationId,
										IdentificationType.REQUEST,
										IdentificationRequestStates.FAILED,
										IdentificationRequestStatusTips.weak_security,
										...keys.map(key => claims[key])
									);
									console.error('ACR value is too low');
									return;
								}
								this.statusService.updateVerificationRequest(
									request.verificationId,
									IdentificationType.REQUEST,
									IdentificationRequestStates.SUCCESS,
									IdentificationRequestStatusTips.success,
									...keys.map(key => claims[key])
								);
							})
							.catch(reason => {
								console.error('JWT decoding failed: ', reason);
								this.statusService.updateVerificationRequest(
									request.verificationId,
									IdentificationType.REQUEST,
									IdentificationRequestStates.ERROR,
									IdentificationRequestStatusTips.failed
								);
							});
						const requestInd = this.pendingRequests.indexOf(request);
						if (0 <= requestInd && requestInd < this.pendingRequests.length) {
							this.pendingRequests.splice(requestInd, 1);
							this.saveModels()
						}
					}
				})
				.catch(reason => {
					console.error('failed to compute JWT hash: ', reason);
					this.statusService.updateVerificationRequest(
						request.verificationId,
						IdentificationType.REQUEST,
						IdentificationRequestStates.ERROR,
						IdentificationRequestStatusTips.failed
					);
				});
		} else {
			console.error(
				'User ID or nonce did not match expected values - marking verification request as failed'
			);
			this.statusService.updateVerificationRequest(
				request.verificationId,
				IdentificationType.REQUEST,
				IdentificationRequestStates.ERROR,
				IdentificationRequestStatusTips.failed
			);
		}
	}
	/* eslint-enable */

	private async sendIdentificationRequest(
		userId: string,
		idToken: string,
		remoteId: string,
		nonce: string,
		registration: Registration,
		verificationId: string,
		type: IdentificationType
	): Promise<void> {
		const payload: IdentificationRequestPayload = {
			jwt: idToken,
			id: userId,
			nonce,
			type: type
		};

		const ctxt = await this.sessionEncryption.encrypt(
			registration.publicKey,
			JSON.stringify(payload)
		);
		const request: IdentificationRequest = {
			payload: ctxt,
			requestId: uuid(),
			verificationId
		};

		console.info(
			`sending verification request [ver-id: ${verificationId}; req-id: ${request.requestId}; user-id: ${userId}]`
		);
		await firstValueFrom(
			this.requestsService.sendMessage(
				this.loginService.apiToken ?? 'an invalid token',
				{
					msg: JSON.stringify(request),
					userId: remoteId
				}
			)
		);
	}

	private handleHttpError(
		er: HttpErrorResponse
	): IdentificationRequestStatusTips {
		console.error(`Backend returned code ${er.status}, body was: `, er.error);
		switch (er.status) {
			case HttpStatusCode.Unauthorized:
				return IdentificationRequestStatusTips.unauthorized;
			case HttpStatusCode.Forbidden:
				return IdentificationRequestStatusTips.forbidden;
			case HttpStatusCode.NotFound:
				return IdentificationRequestStatusTips.not_found;
			case HttpStatusCode.BadGateway:
			case HttpStatusCode.ServiceUnavailable:
			case HttpStatusCode.GatewayTimeout:
				return IdentificationRequestStatusTips.gateway;
			default:
				return IdentificationRequestStatusTips.other;
		}
	}
}
